export const FINANCES_CHART_HEIGHT = 250;

const DEFAULT_TICK_COUNT = 5;

export interface ChartScale {
  // The rounded maximum used to scale bars and the top gridline.
  max: number;
  // Tick values in ascending order, always including 0 and `max`.
  ticks: number[];
}

// Rounds a number to a "nice" 1/2/5 * 10^n value so axis ticks land on
// readable numbers (e.g. 0, 2.5k, 5k) instead of arbitrary maxima.
const niceNumber = (value: number, round: boolean): number => {
  const exponent = Math.floor(Math.log10(value));
  const magnitude = 10 ** exponent;
  const fraction = value / magnitude;

  let niceFraction: number;
  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else if (fraction <= 1) {
    niceFraction = 1;
  } else if (fraction <= 2) {
    niceFraction = 2;
  } else if (fraction <= 5) {
    niceFraction = 5;
  } else {
    niceFraction = 10;
  }

  return niceFraction * magnitude;
};

export const getChartScale = (
  maxValue: number,
  tickCount: number = DEFAULT_TICK_COUNT,
): ChartScale => {
  const safeMax = Math.max(maxValue, 1);
  const step = niceNumber(safeMax / (tickCount - 1), true);
  const niceMax = Math.ceil(safeMax / step) * step;

  const ticks: number[] = [];
  for (let value = 0; value <= niceMax; value += step) {
    ticks.push(value);
  }

  return { max: niceMax, ticks };
};

export const getBarHeight = (
  value: number,
  maxValue: number,
  chartHeight: number,
): number => {
  if (value <= 0) {
    return 0;
  }

  return (value / maxValue) * chartHeight;
};

export const formatCompactCurrency = (valueInCents: number): string => {
  const dollars = valueInCents / 100;

  if (dollars >= 1000) {
    return `$${Math.round(dollars / 100) / 10}k`;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(dollars);
};
